import React from 'react';
import {
  LomavisDoughnutChart1,
  LomavisDoughnutChart1Data
} from '@/lomavisSharedUi/atoms/charts/LomavisDoughnutChart1/LomavisDoughnutChart1';
import { LomavisBarChart2 } from '@/lomavisSharedUi/atoms/charts/LomavisBarChart2/LomavisBarChart2';
import { LomavisLayout1 } from '@/lomavisSharedUi/organisms/layouts/LomavisLayout1/LomavisLayout1';
import { LomavisDataProcessingPlaceholder } from '@/lomavisSharedUi/atoms/placeholders/LomavisDataProcessingPlaceholder';
import { translateComponent } from '@/utils/tolgee/translateComponent';

export interface LomavisChart1Data {
  value: number;
  name: string;
  color?: string;
  percentage?: number;
}
export interface LomavisDoughnutChartAndTable1Props {
  /** The title of the component */
  title: string;
  /** Data for the doughnut chart */
  data: LomavisDoughnutChart1Data[];
  /** Data for the bar chart */

  /** Optional title for the doughnut chart */
  doughnutChartTitle?: string;
  /** Optional title for the bar chart */
  barChartTitle?: string;
  /** Option to show percentage values in bar chart */
  showPercentage?: boolean;
  /** Option to control sort order in bar chart (true = ascending, false = descending) */
  sortAscending?: boolean;
  doughnutChartUseElbowFunction?: boolean;
  doughnutChartShortenLabels?: boolean;
  barChartEnableSorting?: boolean;
}

/**
 * Organisms: LomavisDoughnutChartAndTable1
 * A component that displays a doughnut chart on the left side and a bar chart on the right side
 */

export function applyElbowMethod(
  data: Array<{ name: string; value: number }>,
  minTopItems = 4,
  tolgeeTranslate: any
) {
  const sortedData = [...data].sort((a, b) => b.value - a.value);

  const total = sortedData.reduce((sum, item) => sum + item.value, 0);
  const threshold = 0.02 * total; // Optional: a value cutoff (2% of total)

  const topItems = [];
  let othersValue = 0;

  for (let i = 0; i < sortedData.length; i++) {
    if (i < minTopItems || sortedData[i].value >= threshold) {
      topItems.push(sortedData[i]);
    } else {
      othersValue += sortedData[i].value;
    }
  }

  if (othersValue > 0) {
    topItems.push({
      name: tolgeeTranslate("Others"),
      value: parseFloat(othersValue.toFixed(2))
    });
  }

  return topItems;
}

function shortenNames(data: Array<{ name: string; value: number }>) {
  return data.map((item) => ({
    ...item,
    name: item.name === 'Others' ? 'Others' : item.name.slice(0, 2)
  }));
}

export const LomavisDoughnutChartAndTable1: React.FC<
  LomavisDoughnutChartAndTable1Props
> = ({
  title,
  data,
  doughnutChartTitle,
  barChartTitle,
  doughnutChartUseElbowFunction = false,
  doughnutChartShortenLabels = false,
  showPercentage = true,
  sortAscending = false,
  barChartEnableSorting = true
}) => {

  const t = translateComponent("LomavisDoughnutChartAndTable1");

  const isDataAvailable = data?.length > 0;
  const reducedData = doughnutChartUseElbowFunction
    ? applyElbowMethod(data, 4, t)
    : data;
  const shortenedData = doughnutChartShortenLabels
    ? shortenNames(reducedData)
    : reducedData;

  if (!isDataAvailable) {
    return (
      <LomavisLayout1
        title={title}
        topRowClassName={'h-0px'}
        topLeft={<div />}
        topRight={<div />}
        bottom={<LomavisDataProcessingPlaceholder />}
      />
    );
  }

  return (
    <LomavisLayout1
      title={title}
      topLeftTitle={doughnutChartTitle}
      topRightTitle={barChartTitle}
      topLeft={<LomavisDoughnutChart1 data={shortenedData} />}
      topRight={
        <LomavisBarChart2
          data={data}
          centerVertically={true}
          showPercentage={showPercentage}
          sortAscending={sortAscending}
          enableSorting={barChartEnableSorting}
        />
      }
      bottom={<div />} // Empty element for the bottom part
    />
  );
};
